/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { get as levenshtein } from 'fast-levenshtein';
import { AutomationsApi } from '@src/services/automations/automations-api';
import { getSource, TSources } from './utils/sources';
import { LutienApi } from '@src/services/lutien/lutien-api';
import { HubApi } from '@src/services/hub/hub-api';
import { AutomationsView } from './AutomationsView';

export const AutomationsContainer = () => {
  const [currentLevel, setCurrentLevel] = useState<Record<string, any> | null>(null);
  const [pathStack, setPathStack] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [dataSource, setDataSource] = useState<TSources>(() => {
    const storedDataSource = localStorage.getItem('automationsDataSource');
    return (storedDataSource as TSources) || 'default';
  });
  const [port, setPort] = useState(() => {
    const storedPort = localStorage.getItem('automationsPort');
    return storedPort ? parseInt(storedPort, 10) : 5000;
  });
  const [debouncedPort, setDebouncedPort] = useState(port);
  const [progress, setProgress] = useState(0);
  const queryClient = useQueryClient();
  const [eventHandlersEnabled, setEventHandlersEnabled] = useState(
    () => queryClient.getQueryData(['eventHandlers']) !== undefined,
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedFlowPath = searchParams.get('flow');

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedPort(port);
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [port]);

  useEffect(() => {
    localStorage.setItem('automationsDataSource', dataSource);
  }, [dataSource]);

  useEffect(() => {
    localStorage.setItem('automationsPort', port.toString());
  }, [port]);

  const automationsApi = new AutomationsApi(undefined, getSource(dataSource, debouncedPort));

  const { data: flowListResponse, isLoading } = useQuery({
    queryKey: ['getFlowsList', dataSource, debouncedPort],
    queryFn: () =>
      automationsApi.v1.flows.getList({ settings: { maxRetries: 1, retryDelay: 200, delayType: 'linear' } }),
    refetchInterval: dataSource === 'localhost' ? 60000 : false,
  });

  const flowList = flowListResponse
    ? (Object.values(flowListResponse).filter((value) => typeof value === 'string') as string[])
    : [];

  const {
    data: eventHandlers,
    isLoading: isLoadingEventHandlers,
    isFetching: isFetchingEventHandlers,
    refetch: refetchEventHandlers,
  } = useQuery({
    queryKey: ['eventHandlers'],
    queryFn: async () => {
      setProgress(0);
      try {
        const lutienApi = new LutienApi();
        const hubApi = new HubApi();

        const organizationsResponse = await lutienApi.v1.organizations.get();
        const activeOrgs = organizationsResponse
          .filter((o) => o.status === 'ACTIVE')
          .map((org) => ({
            id: org.id,
            name: org.fantasyName,
          }));

        const totalOrgs = activeOrgs.length;
        if (totalOrgs === 0) {
          setProgress(100);
          return {};
        }

        const chunkSize = 50;
        const eventByURL = {};
        let processedOrgs = 0;

        for (let i = 0; i < totalOrgs; i += chunkSize) {
          const orgChunk = activeOrgs.slice(i, i + chunkSize);

          const chunkResults = await Promise.all(
            orgChunk.map((org) =>
              hubApi.v1.crm.eventHandlers.get({
                params: { organizationId: org.id },
              }),
            ),
          );

          processedOrgs += orgChunk.length;
          setProgress(Math.round((processedOrgs / totalOrgs) * 100));

          chunkResults
            .flat()
            .filter((e) => e.enabled && e.callbackUrl.includes(import.meta.env.VITE_AUTOMATIONS_BASE_URL))
            .forEach((event) => {
              eventByURL[event.callbackUrl] = eventByURL[event.callbackUrl] || [];
              eventByURL[event.callbackUrl].push({
                callbackUrl: event.callbackUrl,
                source: event.source,
                detailType: event.detailType,
              });
            });
        }

        setProgress(100);
        return eventByURL;
      } catch (error) {
        console.error('Error in queryFn:', error);
        setProgress(0);
        throw error;
      }
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: eventHandlersEnabled,
  });

  const createTree = (paths: string[]): Record<string, any> => {
    const tree: Record<string, any> = {};
    paths.forEach((path) => {
      const parts = path.split('/');
      let currentLevel = tree;
      parts.forEach((part, index) => {
        if (index != 0) {
          if (!currentLevel[part]) {
            currentLevel[part] = index === parts.length - 1 ? null : {};
          }
          currentLevel = currentLevel[part];
        }
      });
    });
    return tree;
  };

  const goToLevel = (depth: number) => {
    if (depth < 0 || depth >= pathStack.length) return;
    const newStack = pathStack.slice(0, depth);
    let newLevel = createTree(flowList || []);
    newStack.forEach((key) => {
      newLevel = newLevel[key];
    });
    setPathStack(newStack);
    setCurrentLevel(depth === 0 ? null : newLevel);
    setSearchTerm('');
  };

  const goBack = () => goToLevel(pathStack.length - 1);

  const handleItemClick = (key: string, value: unknown) => {
    if (value) {
      setSearchTerm('');
      setPathStack((prev) => [...prev, key]);
      setCurrentLevel(value);
    } else {
      setSearchParams({ flow: `/${[...pathStack, key].join('/')}` });
    }
  };

  const handleCloseFlow = () => setSearchParams({});

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSourceChange = (newSource: TSources, newPort: number) => {
    setPort(newPort);
    setDataSource(newSource);
  };

  const treeData = flowList ? createTree(flowList) : {};

  const hasSyncedFromUrlRef = useRef(false);
  useEffect(() => {
    if (hasSyncedFromUrlRef.current || !selectedFlowPath || flowList.length === 0) return;
    hasSyncedFromUrlRef.current = true;
    const parentSegments = selectedFlowPath.split('/').filter(Boolean).slice(0, -1);
    let level: Record<string, any> | null = createTree(flowList);
    for (const segment of parentSegments) {
      level = level?.[segment] ?? null;
      if (!level) return;
    }
    setPathStack(parentSegments);
    setCurrentLevel(parentSegments.length > 0 ? level : null);
  }, [selectedFlowPath, flowList.length]);
  const displayLevel = currentLevel || treeData;

  const getFilteredItems = (): [string, unknown][] => {
    if (!searchTerm) return Object.entries(displayLevel);

    const searchTermLower = searchTerm.toLowerCase();
    const threshold = 0.1;

    return Object.entries(displayLevel)
      .map(([key, value]) => {
        const keyLower = key.toLowerCase();
        const distance = levenshtein(keyLower, searchTermLower);
        const maxLength = Math.max(keyLower.length, searchTermLower.length);
        const similarity = 1 - distance / maxLength;
        return { key, value, similarity };
      })
      .filter((item) => item.similarity >= threshold)
      .sort((a, b) => b.similarity - a.similarity)
      .map(({ key, value }) => [key, value]);
  };

  const filteredItems = getFilteredItems();

  return (
    <AutomationsView
      isLoading={isLoading}
      isLoadingEventHandlers={isLoadingEventHandlers || isFetchingEventHandlers}
      progress={progress}
      eventHandlers={eventHandlers}
      pathStack={pathStack}
      searchTerm={searchTerm}
      filteredItems={filteredItems}
      dataSource={dataSource}
      port={port}
      eventHandlersEnabled={eventHandlersEnabled}
      onEventHandlersToggle={setEventHandlersEnabled}
      onReloadEvents={() => refetchEventHandlers()}
      onGoBack={goBack}
      onGoToLevel={goToLevel}
      selectedFlowPath={selectedFlowPath}
      onCloseFlow={handleCloseFlow}
      onItemClick={handleItemClick}
      onSearchChange={handleSearchChange}
      onSourceChange={handleSourceChange}
    />
  );
};
